import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import CartaoPergunta from './CartaoPergunta';
import PreviaFormulario from './PreviaFormulario';
import { TIPOS_CONDICIONAVEIS, TIPOS_COM_OPCOES } from '../constants';
import {
  condicaoValorParaJson,
  jsonParaCondicaoValor,
  mensagemErro,
  novaChave,
  rolarParaPrimeiroErro,
} from '../utils';
import type { Pergunta } from '../types';

export interface ConstrutorFormularioHandle {
  salvar: () => Promise<boolean>;
}

interface ConstrutorFormularioProps {
  modalidadeId: number;
  formularioId: number | null;
  inscritos: number;
  onFormularioCriado?: (id: number) => void;
}

interface PerguntaDB {
  id: number;
  ordem: number;
  enunciado: string;
  descricao: string | null;
  tipo: Pergunta['tipo'];
  obrigatoria: boolean;
  opcoes: string[] | null;
  condicao_pergunta_id: number | null;
  condicao_valor: unknown;
}

function perguntasComuns(): Pergunta[] {
  const chaves = Array.from({ length: 10 }, () => novaChave());
  const q = (i: number) => chaves[i];

  const lista: Pergunta[] = [
    {
      chave: q(0),
      id: null,
      enunciado: 'Em qual equipe você deseja servir?',
      descricao: '',
      tipo: 'escolha_unica',
      obrigatoria: true,
      opcoes: ['Cozinha', 'Liturgia', 'Música', 'Intercessão', 'Recepção', 'Ordem', 'Onde precisarem'],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(1),
      id: null,
      enunciado: 'Tamanho da camiseta',
      descricao: '',
      tipo: 'escolha_unica',
      obrigatoria: true,
      opcoes: ['PP', 'P', 'M', 'G', 'GG', 'XG'],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(2),
      id: null,
      enunciado: 'Você possui alguma alergia?',
      descricao: '',
      tipo: 'sim_nao',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(3),
      id: null,
      enunciado: 'Qual alergia você possui?',
      descricao: '',
      tipo: 'texto_curto',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: q(2),
      condicaoValor: 'true',
      respondida: false,
    },
    {
      chave: q(4),
      id: null,
      enunciado: 'Você possui alguma restrição alimentar?',
      descricao: '',
      tipo: 'sim_nao',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(5),
      id: null,
      enunciado: 'Qual restrição alimentar?',
      descricao: '',
      tipo: 'texto_curto',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: q(4),
      condicaoValor: 'true',
      respondida: false,
    },
    {
      chave: q(6),
      id: null,
      enunciado: 'Faz uso de medicamento contínuo?',
      descricao: '',
      tipo: 'sim_nao',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(7),
      id: null,
      enunciado: 'Qual medicamento e em qual horário?',
      descricao: '',
      tipo: 'texto_longo',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: q(6),
      condicaoValor: 'true',
      respondida: false,
    },
    {
      chave: q(8),
      id: null,
      enunciado: 'Nome do contato de emergência',
      descricao: '',
      tipo: 'texto_curto',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
    {
      chave: q(9),
      id: null,
      enunciado: 'Telefone do contato de emergência',
      descricao: '',
      tipo: 'telefone',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    },
  ];
  return lista;
}

function ConstrutorFormulario(
  { modalidadeId, formularioId, inscritos, onFormularioCriado }: ConstrutorFormularioProps,
  ref: React.Ref<ConstrutorFormularioHandle>,
) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [previa, setPrevia] = useState(false);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [avisoRemocao, setAvisoRemocao] = useState(false);
  const [avisoRespondida, setAvisoRespondida] = useState(false);
  // Formulário criado por esta própria tela: as perguntas já estão na tela,
  // não recarregar do banco (era o que apagava as 10 perguntas depois de salvar).
  const formIdProprioRef = useRef<number | null>(null);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      if (!formularioId) return;
      if (formIdProprioRef.current === formularioId) return;
      setCarregando(true);
      setErroGeral(null);
      try {
        const { data: form } = await supabase
          .from('formularios')
          .select('nome, descricao')
          .eq('id', formularioId)
          .maybeSingle();
        if (ativo && form) {
          setNome((form as { nome: string }).nome || '');
          setDescricao((form as { descricao: string | null }).descricao || '');
        }

        const { data: perguntasData, error: errPerguntas } = await supabase
          .from('perguntas')
          .select('*')
          .eq('formulario_id', formularioId)
          .order('ordem');
        if (errPerguntas) throw errPerguntas;

        const linhas = (perguntasData as PerguntaDB[]) || [];
        const ids = linhas.map((l) => l.id);

        let respondidos = new Set<number>();
        if (ids.length > 0) {
          const { data: respostas } = await supabase
            .from('respostas')
            .select('pergunta_id')
            .in('pergunta_id', ids);
          respondidos = new Set(
            ((respostas as { pergunta_id: number }[]) || []).map((r) => r.pergunta_id),
          );
        }

        const chavePorId = new Map<number, string>();
        linhas.forEach((l) => chavePorId.set(l.id, `q-${l.id}`));

        const lista: Pergunta[] = linhas.map((l) => ({
          chave: `q-${l.id}`,
          id: l.id,
          enunciado: l.enunciado,
          descricao: l.descricao || '',
          tipo: l.tipo,
          obrigatoria: l.obrigatoria,
          opcoes: Array.isArray(l.opcoes) ? l.opcoes.map(String) : [],
          condicaoChave: l.condicao_pergunta_id
            ? chavePorId.get(l.condicao_pergunta_id) || null
            : null,
          condicaoValor: jsonParaCondicaoValor(l.condicao_valor),
          respondida: respondidos.has(l.id),
        }));

        if (ativo) setPerguntas(lista);
      } catch (e) {
        if (ativo) {
          setErroGeral(
            mensagemErro(e, 'Não foi possível carregar a ficha. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [formularioId]);

  function condicionaveisPara(chave: string): Pergunta[] {
    const idx = perguntas.findIndex((p) => p.chave === chave);
    return perguntas
      .slice(0, idx)
      .filter((p) => TIPOS_CONDICIONAVEIS.includes(p.tipo) && !p.condicaoChave);
  }

  async function salvar(): Promise<boolean> {
    const novos: Record<string, string> = {};
    if (!nome.trim()) novos.nome = 'Digite o nome do formulário.';
    perguntas.forEach((p) => {
      if (!p.enunciado.trim()) novos[`${p.chave}:enunciado`] = 'Digite a pergunta.';
      if (p.tipo === 'lista') {
        if (p.opcoes.filter((o) => o.trim()).length < 1) {
          novos[`${p.chave}:opcoes`] = 'A lista precisa de pelo menos uma coluna.';
        }
      } else if (
        TIPOS_COM_OPCOES.includes(p.tipo) &&
        p.opcoes.filter((o) => o.trim()).length < 2
      ) {
        novos[`${p.chave}:opcoes`] =
          'Uma pergunta de escolha precisa de pelo menos duas opções.';
      }
    });
    setErros(novos);
    if (Object.keys(novos).length > 0) {
      rolarParaPrimeiroErro();
      return false;
    }

    setSalvando(true);
    setErroGeral(null);
    try {
      let formId = formularioId;
      if (!formId) {
        const { data, error } = await supabase
          .from('formularios')
          .insert({
            modalidade_id: modalidadeId,
            nome: nome.trim(),
            descricao: descricao.trim() || null,
            ativo: true,
          })
          .select('id')
          .single();
        if (error) throw error;
        formId = (data as { id: number }).id;
        formIdProprioRef.current = formId;
        if (onFormularioCriado) onFormularioCriado(formId);
      } else {
        const { error } = await supabase
          .from('formularios')
          .update({ nome: nome.trim(), descricao: descricao.trim() || null, ativo: true })
          .eq('id', formId);
        if (error) throw error;
      }

      // Fase 1: grava as perguntas sem condição, garantindo o id de cada uma.
      const chavePorId = new Map<string, number>();
      const lista = [...perguntas];
      for (let i = 0; i < lista.length; i += 1) {
        const p = lista[i];
        const base = {
          enunciado: p.enunciado.trim(),
          descricao: p.descricao || null,
          tipo: p.tipo,
          obrigatoria: p.obrigatoria,
          opcoes: TIPOS_COM_OPCOES.includes(p.tipo) ? p.opcoes.filter((o) => o.trim()) : null,
          ordem: i,
        };
        if (p.id) {
          const { error } = await supabase.from('perguntas').update(base).eq('id', p.id);
          if (error) throw error;
          chavePorId.set(p.chave, p.id);
        } else {
          const { data, error } = await supabase
            .from('perguntas')
            .insert({ ...base, formulario_id: formId })
            .select('id')
            .single();
          if (error) throw error;
          const novoId = (data as { id: number }).id;
          lista[i] = { ...p, id: novoId };
          chavePorId.set(p.chave, novoId);
        }
      }

      // Remove perguntas que saíram da lista (sem resposta gravada).
      const idsAtuais = lista.filter((p) => p.id).map((p) => p.id as number);
      const { data: existentes } = await supabase
        .from('perguntas')
        .select('id')
        .eq('formulario_id', formId);
      const idsExistentes = ((existentes as { id: number }[]) || []).map((e) => e.id);
      const removidas = idsExistentes.filter((id) => !idsAtuais.includes(id));
      if (removidas.length > 0) {
        const { data: respostas } = await supabase
          .from('respostas')
          .select('pergunta_id')
          .in('pergunta_id', removidas);
        const comResposta = new Set(
          ((respostas as { pergunta_id: number }[]) || []).map((r) => r.pergunta_id),
        );
        const paraDeletar = removidas.filter((id) => !comResposta.has(id));
        if (paraDeletar.length > 0) {
          const { error } = await supabase.from('perguntas').delete().in('id', paraDeletar);
          if (error) throw error;
        }
      }

      // Fase 2: grava as condicionais.
      for (const p of lista) {
        if (!p.id) continue;
        const condicaoPerguntaId = p.condicaoChave
          ? chavePorId.get(p.condicaoChave) ?? null
          : null;
        const { error } = await supabase
          .from('perguntas')
          .update({
            condicao_pergunta_id: condicaoPerguntaId,
            condicao_valor: condicaoValorParaJson(p.condicaoValor),
          })
          .eq('id', p.id);
        if (error) throw error;
      }

      setPerguntas(lista);
      setErros({});
      return true;
    } catch (e) {
      setErroGeral(
        mensagemErro(e, 'Não foi possível salvar a ficha. Tente novamente.'),
      );
      return false;
    } finally {
      setSalvando(false);
    }
  }

  useImperativeHandle(ref, () => ({ salvar }));

  function mudarPergunta(chave: string, patch: Partial<Pergunta>) {
    setPerguntas((prev) => prev.map((p) => (p.chave === chave ? { ...p, ...patch } : p)));
    if (patch.enunciado && erros[`${chave}:enunciado`]) {
      setErros((e) => ({ ...e, [`${chave}:enunciado`]: '' }));
    }
    if (patch.opcoes && erros[`${chave}:opcoes`]) {
      setErros((e) => ({ ...e, [`${chave}:opcoes`]: '' }));
    }
  }

  function moverPergunta(indice: number, direcao: -1 | 1) {
    const destino = indice + direcao;
    if (destino < 0 || destino >= perguntas.length) return;
    setPerguntas((prev) => {
      const novo = [...prev];
      const [item] = novo.splice(indice, 1);
      novo.splice(destino, 0, item);

      // Revalida condições: desfaz a condição se a pergunta dependida mudou de lugar.
      const revalidado = novo.map((p) => {
        if (!p.condicaoChave) return p;
        const idxDep = novo.findIndex((x) => x.chave === p.condicaoChave);
        const idxAtual = novo.findIndex((x) => x.chave === p.chave);
        if (idxDep === -1 || idxDep >= idxAtual) {
          return { ...p, condicaoChave: null, condicaoValor: null };
        }
        return p;
      });

      const quebrou = revalidado.some(
        (p, idx) => novo[idx].condicaoChave !== null && p.condicaoChave === null,
      );
      if (quebrou) setAvisoRemocao(true);
      return revalidado;
    });
  }

  function removerPergunta(indice: number) {
    const alvo = perguntas[indice];
    if (alvo.respondida) {
      setAvisoRemocao(false);
      setAvisoRespondida(true);
      return;
    }
    setPerguntas((prev) => {
      const restantes = prev.filter((_, i) => i !== indice);
      const removidaChave = alvo.chave;
      const quebrou = restantes.some((x) => x.condicaoChave === removidaChave);
      // Perguntas que dependiam da removida perdem a condição.
      const revalidado = restantes.map((p) =>
        p.condicaoChave === removidaChave ? { ...p, condicaoChave: null, condicaoValor: null } : p,
      );
      if (quebrou) setAvisoRemocao(true);
      return revalidado;
    });
  }

  function duplicarPergunta(indice: number) {
    const original = perguntas[indice];
    const copia: Pergunta = {
      ...original,
      chave: novaChave(),
      id: null,
      respondida: false,
    };
    setPerguntas((prev) => {
      const novo = [...prev];
      novo.splice(indice + 1, 0, copia);
      return novo;
    });
    setSelecionada(copia.chave);
  }

  function adicionarPergunta() {
    const nova: Pergunta = {
      chave: novaChave(),
      id: null,
      enunciado: '',
      descricao: '',
      tipo: 'texto_curto',
      obrigatoria: true,
      opcoes: [],
      condicaoChave: null,
      condicaoValor: null,
      respondida: false,
    };
    setPerguntas((prev) => [...prev, nova]);
    setSelecionada(nova.chave);
  }

  function adicionarComuns() {
    setPerguntas((prev) => [...prev, ...perguntasComuns()]);
  }

  if (carregando) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-white p-6">
        <div className="h-5 w-48 animate-pulse rounded bg-secondary-100" />
        <div className="mt-3 h-24 w-full animate-pulse rounded bg-secondary-100" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-secondary-200 bg-white" aria-busy={salvando}>
      <div className="border-b border-secondary-100 p-5">
        <h3 className="text-base font-medium text-foreground-950">Construtor de formulário</h3>
        <p className="mt-1 text-sm text-secondary-500">
          Monte as perguntas que o participante vai responder, no estilo de um formulário de edição.
        </p>

        {inscritos > 0 && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Esta ficha já foi respondida por {inscritos} {inscritos === 1 ? 'pessoa' : 'pessoas'}.
            Perguntas novas ficarão em branco para quem já se inscreveu.
          </div>
        )}

        {avisoRemocao && (
          <div className="mt-3 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span>
              A condição foi removida porque a pergunta de que ela dependia mudou de lugar.
            </span>
            <button
              type="button"
              onClick={() => setAvisoRemocao(false)}
              className="cursor-pointer text-amber-800"
              aria-label="Fechar aviso"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {avisoRespondida && (
          <div className="mt-3 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span>
              Esta pergunta já foi respondida por alguém, então não pode ser removida. Se quiser
              escondê-la dos novos inscritos, você pode desmarcar &quot;Obrigatória&quot; ou editar
              o texto.
            </span>
            <button
              type="button"
              onClick={() => setAvisoRespondida(false)}
              className="cursor-pointer text-amber-800"
              aria-label="Fechar aviso"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        <div className="mt-4">
          <CampoTexto
            id="form-nome"
            name="nome"
            label="Nome do formulário"
            help="Serve apenas para sua organização. Ex.: Ficha de inscrição da equipe."
            value={nome}
            onChange={(v) => {
              setNome(v);
              if (erros.nome) setErros((e) => ({ ...e, nome: '' }));
            }}
            required
            error={erros.nome || null}
            placeholder="Ficha de inscrição da equipe"
          />
        </div>

        <div className="mt-4">
          <CampoArea
            id="form-descricao"
            name="descricao"
            label="Introdução da ficha (opcional)"
            help="Aparece antes da primeira pergunta. É onde entram o valor, o que está incluso e os avisos importantes."
            value={descricao}
            onChange={setDescricao}
            placeholder="Ex.: O valor da inscrição é R$ 80 e inclui refeições e alojamento…"
          />
        </div>
      </div>

      <div className="space-y-3 p-5">
        {perguntas.map((p, i) => (
          <CartaoPergunta
            key={p.chave}
            pergunta={p}
            indice={i}
            condicionaveis={condicionaveisPara(p.chave)}
            selecionado={selecionada === p.chave}
            podeSubir={i > 0}
            podeDescer={i < perguntas.length - 1}
            erroEnunciado={erros[`${p.chave}:enunciado`]}
            erroOpcoes={erros[`${p.chave}:opcoes`]}
            aoSelecionar={() => setSelecionada((prev) => (prev === p.chave ? null : p.chave))}
            aoMudar={(patch) => mudarPergunta(p.chave, patch)}
            aoSubir={() => moverPergunta(i, -1)}
            aoDescer={() => moverPergunta(i, 1)}
            aoRemover={() => removerPergunta(i)}
            aoDuplicar={() => duplicarPergunta(i)}
          />
        ))}

        {perguntas.length === 0 && (
          <p className="rounded-lg border border-dashed border-secondary-300 px-4 py-6 text-center text-sm text-secondary-500">
            Nenhuma pergunta ainda. Adicione a primeira abaixo.
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={adicionarPergunta}
            className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-add-line" />
            </span>
            Adicionar pergunta
          </button>
          <button
            type="button"
            onClick={adicionarComuns}
            className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-magic-line" />
            </span>
            Usar perguntas comuns de acampamento
          </button>
        </div>

        {erroGeral && (
          <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {erroGeral}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setPrevia(true)}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-secondary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-eye-line" />
            </span>
            Ver como fica
          </button>
        </div>
      </div>

      {previa && (
        <PreviaFormulario nome={nome} perguntas={perguntas} aoFechar={() => setPrevia(false)} />
      )}
    </div>
  );
}

export default forwardRef(ConstrutorFormulario);